import { useEffect, useRef, useState } from 'react'
// Das Stylesheet von xterm.js gehört zwingend dazu. Ohne es bleibt die
// Bildlauffläche sichtbar, wächst mit der Ausgabe und schiebt die Zeilen aus
// dem Fenster — das Terminal wird schwarz, sobald mehr als ein Bildschirm
// voll Text ankommt. Es wird fest eingebunden und nicht erst mit dem
// Terminal nachgeladen, damit es sicher vor dem ersten Bild da ist.
import '@xterm/xterm/css/xterm.css'
import type { Device } from '../lib/types'
import { customerColor, parseControl, resizeMessage, terminalSocketUrl } from '../lib/remote'

interface Props {
  device: Device
  color?: string
  onClose: () => void
}

type Status = 'verbinde' | 'verbunden' | 'getrennt'

/**
 * Das Sitzungsfenster für SSH.
 *
 * Es trägt oben die Kennfarbe des Kunden, damit auf einen Blick sichtbar ist,
 * in wessen Netz getippt wird (docs/sicherheit.md). Das Terminal selbst ist
 * xterm.js; es wird erst beim Öffnen geladen, damit die übrige Oberfläche
 * leicht bleibt.
 */
export function SshFenster({ device, color, onClose }: Props) {
  const host = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<Status>('verbinde')
  const [note, setNote] = useState<string | null>(null)
  const [hostKey, setHostKey] = useState<string | null>(null)

  useEffect(() => {
    let socket: WebSocket | null = null
    let dispose: (() => void) | null = null
    let cancelled = false

    async function start() {
      const [{ Terminal }, { FitAddon }] = await Promise.all([
        import('@xterm/xterm'),
        import('@xterm/addon-fit'),
      ])
      if (cancelled || !host.current) return

      const term = new Terminal({
        fontFamily: 'var(--font-mono), ui-monospace, monospace',
        fontSize: 13,
        cursorBlink: true,
        theme: { background: '#0e1115', foreground: '#e7eaee' },
      })
      const fit = new FitAddon()
      term.loadAddon(fit)
      term.open(host.current)

      const anpassen = () => {
        try {
          fit.fit()
        } catch {
          // In sehr kleinen Fenstern schlägt die Messung fehl; die Vorgabe bleibt.
        }
      }
      anpassen()
      // Die Schrift kommt als Webfont nach. Vorher gemessene Zeichenbreiten
      // stimmen danach nicht mehr, deshalb noch einmal messen.
      void document.fonts?.ready.then(anpassen).catch(() => undefined)

      socket = new WebSocket(terminalSocketUrl(device.id, term.cols, term.rows))
      socket.binaryType = 'arraybuffer'

      socket.onmessage = (event: MessageEvent<ArrayBuffer | string>) => {
        if (typeof event.data === 'string') {
          const message = parseControl(event.data)
          if (!message) return
          if (message.type === 'ready') {
            setStatus('verbunden')
            setHostKey(message.host_key ?? null)
          } else if (message.type === 'error' || message.type === 'closed') {
            setNote(message.message ?? 'Die Sitzung wurde beendet.')
          }
          return
        }
        term.write(new Uint8Array(event.data))
      }
      socket.onclose = () => setStatus('getrennt')
      socket.onerror = () => setNote('Die Verbindung wurde unterbrochen.')

      const typed = term.onData((data) => {
        if (socket?.readyState === WebSocket.OPEN) socket.send(new TextEncoder().encode(data))
      })
      const resized = term.onResize(({ cols, rows }) => {
        if (socket?.readyState === WebSocket.OPEN) socket.send(resizeMessage(cols, rows))
      })
      window.addEventListener('resize', anpassen)
      // Nicht nur das Fenster ändert die Größe: erscheint oben der
      // Hinweisstreifen, schrumpft der Bereich darunter, ohne dass jemand am
      // Fenster zieht. Ohne diese Messung wären die untersten Zeilen ab da
      // abgeschnitten.
      const beobachter =
        typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => anpassen())
      if (host.current) beobachter?.observe(host.current)

      dispose = () => {
        window.removeEventListener('resize', anpassen)
        beobachter?.disconnect()
        typed.dispose()
        resized.dispose()
        term.dispose()
      }
      term.focus()
    }

    void start()
    return () => {
      cancelled = true
      dispose?.()
      socket?.close()
    }
  }, [device.id])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Strg+Umschalt+Q beendet, ohne mit den Tasten des Geräts zu kollidieren.
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'q') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label={`SSH zu ${device.name}`}>
      <div className="terminal-window">
        <div className="kennfarbe" style={{ background: customerColor(color) }} aria-hidden="true" />
        <header className="terminal-head">
          <div className="stack" style={{ gap: 2 }}>
            <strong>
              {device.customer_name} · {device.site_name} · <span className="mono">{device.name}</span>
            </strong>
            <span className="muted" style={{ fontSize: 12 }}>
              <span className="mono">
                {device.mgmt_ip}:{device.ssh_port}
              </span>{' '}
              über den Agent · Sitzung wird aufgezeichnet
              {hostKey ? ` · Hostschlüssel ${hostKey}` : ''}
            </span>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <span className={`badge ${status === 'verbunden' ? 'badge-ok' : status === 'verbinde' ? 'badge-info' : 'badge-off'}`}>
              {status === 'verbunden' ? 'Verbunden' : status === 'verbinde' ? 'Verbinde …' : 'Getrennt'}
            </span>
            <button type="button" className="btn btn-sm" onClick={onClose}>
              Trennen
            </button>
          </div>
        </header>
        {note ? (
          <p className="terminal-note" role="status">
            {note}
          </p>
        ) : null}
        <div className="terminal-body" ref={host} />
      </div>
    </div>
  )
}
